import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatCreatedDate } from '../constants/utils';
import api from '../helpers/api';

const STATUS_BADGE = {
  UPCOMING: { color: 'info', labelKey: 'contracts.status_upcoming', fallback: 'Upcoming' },
  ACTIVE: { color: 'success', labelKey: 'contracts.status_active', fallback: 'Active' },
  EXPIRING_SOON: { color: 'warning', labelKey: 'contracts.status_expiring_soon', fallback: 'Expiring Soon' },
  EXPIRED: { color: 'danger', labelKey: 'contracts.status_expired', fallback: 'Expired' },
};

const formatDDMMYYYY = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const ContractCategoryModal = ({ isOpen, toggle, categoryData, loading, onEditCategory = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // One currency per org (no FX conversion) — show the org's current currency, not the
  // per-contract snapshot. See currency-uniformity work.
  const orgCurrency = useSelector((s) => s.ActiveContext?.activeOrganizationCurrency) || 'USD';
  const [activeTab, setActiveTab] = useState('info');
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  const fetchContracts = useCallback(async () => {
    if (!categoryData?.id) return;
    setContractsLoading(true);
    try {
      const { data } = await api.get(`contract/get-all?contractCategoryId=${categoryData.id}`);
      setContracts(data?.response || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
    } finally {
      setContractsLoading(false);
    }
  }, [categoryData?.id]);

  useEffect(() => {
    if (activeTab === 'contracts' && categoryData?.id) {
      fetchContracts();
    }
  }, [activeTab, categoryData?.id, fetchContracts]);

  useEffect(() => {
    if (isOpen) setActiveTab('info');
  }, [isOpen]);

  if (!categoryData && !loading) return null;

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Header */}
        <div
          className="modal-header border-0 text-white position-sticky"
          style={{
            top: 0,
            zIndex: 10,
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
            padding: '1.5rem',
          }}
        >
          <div className="d-flex align-items-center">
            <div
              className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: '40px', height: '40px' }}
            >
              <i className="bx bx-file text-white fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title mb-0 fw-bold text-white">
                {categoryData?.name || t('contract_categories.modal.title_default', 'Contract Category Details')}
              </h5>
              <p className="text-white-50 small mb-0">
                {t('contract_categories.modal.subtitle', 'Contract category information and usage')}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 ms-auto">
            {onEditCategory && categoryData && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                onClick={() => onEditCategory(categoryData)}
                title={t('contract_categories.edit_category', 'Edit Contract Category')}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  color: '#495057',
                  fontWeight: '500',
                  borderRadius: '8px',
                }}
              >
                <FeatherIcon icon="edit-3" size={16} />
                <span>{t('contract_categories.edit_category', 'Edit Contract Category')}</span>
              </button>
            )}
            <button
              onClick={toggle}
              type="button"
              className="btn-close btn-close-white"
              aria-label={t('shared.close', 'Close')}
            />
          </div>
        </div>

        {/* Tabs */}
        <Nav tabs className="border-bottom-0 px-4 pt-3 bg-light" style={{ borderRadius: '0' }}>
          <NavItem>
            <NavLink
              className={activeTab === 'info' ? 'active' : ''}
              onClick={() => setActiveTab('info')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'info' ? '#fff' : 'transparent',
                color: activeTab === 'info' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'info' ? '600' : '400',
                cursor: 'pointer',
              }}
            >
              <i className="bx bx-info-circle me-2"></i>
              {t('shared.info', 'Info')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'contracts' ? 'active' : ''}
              onClick={() => setActiveTab('contracts')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'contracts' ? '#fff' : 'transparent',
                color: activeTab === 'contracts' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'contracts' ? '600' : '400',
                cursor: 'pointer',
              }}
            >
              <i className="bx bx-file me-2"></i>
              {t('contracts.title', 'Contracts')}
            </NavLink>
          </NavItem>
        </Nav>

        {/* Body */}
        <div className="modal-body p-0" style={{ maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}>
          <TabContent activeTab={activeTab} className="h-100">
            {/* Info tab */}
            <TabPane tabId="info">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: '40px', height: '40px' }}
                      >
                        <i className="bx bx-file text-primary fs-5"></i>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">
                          {t('contract_categories.modal.info_title', 'Category Information')}
                        </h5>
                        <p className="text-muted small mb-0">
                          {t('contract_categories.modal.info_subtitle', 'Full category details')}
                        </p>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">
                          {t('contract_categories.modal.loading_details', 'Loading category details...')}
                        </p>
                      </div>
                    ) : (
                      categoryData && (
                        <Row className="g-4">
                          <Col md={6}>
                            <div className="bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-info-circle me-2"></i>
                                {t('shared.basic_information', 'Basic Information')}
                              </h6>
                              <div className="mb-3">
                                <label className="text-muted small">{t('shared.name', 'Name')}</label>
                                <div className="fw-medium">{categoryData?.name || '-'}</div>
                              </div>
                              <div>
                                <label className="text-muted small">{t('shared.description', 'Description')}</label>
                                <div className="fw-medium">
                                  {categoryData?.description || t('shared.no_description', 'No description')}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-time me-2"></i>
                                {t('shared.activity', 'Activity')}
                              </h6>
                              {categoryData?.createdAt && (
                                <div className="mb-3">
                                  <div className="fw-medium text-muted">
                                    {t('shared.created_by_on', 'Created {{user}} on {{date}}', {
                                      user: '',
                                      date: formatCreatedDate(categoryData?.createdAt),
                                    })}
                                  </div>
                                </div>
                              )}
                              {categoryData?.updatedAt && categoryData?.createdAt !== categoryData?.updatedAt && (
                                <div>
                                  <div className="fw-medium text-muted">
                                    {t('shared.last_updated_by_on', 'Last updated {{user}} on {{date}}', {
                                      user: '',
                                      date: formatCreatedDate(categoryData?.updatedAt),
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      )
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Contracts tab */}
            <TabPane tabId="contracts">
              <div className="p-4">
                {contractsLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="text-muted mt-3">
                      {t('contract_categories.modal.loading_contracts', 'Loading contracts...')}
                    </p>
                  </div>
                ) : contracts.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-file text-muted" style={{ fontSize: '3rem' }}></i>
                    <h5 className="text-muted mt-3 mb-2">
                      {t('contract_categories.modal.no_contracts', 'No contracts yet')}
                    </h5>
                    <p className="text-muted">
                      {t('contract_categories.modal.no_contracts_desc', "No contracts use this category yet.")}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>{t('contracts.contract_number', 'Contract #')}</th>
                          <th>{t('contracts.vendor', 'Vendor')}</th>
                          <th>{t('contracts.start_date', 'Start Date')}</th>
                          <th>{t('contracts.end_date', 'End Date')}</th>
                          <th>{t('contracts.value', 'Value')}</th>
                          <th>{t('contracts.status', 'Status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contracts.map((c) => {
                          const badge = STATUS_BADGE[c.status] || STATUS_BADGE.ACTIVE;
                          return (
                            <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => { toggle(); navigate(`/contracts/${c.id}`); }}>
                              <td className="text-primary">{c.contractNumber}</td>
                              <td>{c.vendorName || '-'}</td>
                              <td>{formatDDMMYYYY(c.startDate) || '-'}</td>
                              <td>{formatDDMMYYYY(c.endDate) || '-'}</td>
                              <td>
                                {c.contractValue != null
                                  ? `${c.contractValue} ${orgCurrency}`.trim()
                                  : '-'}
                              </td>
                              <td>
                                <Badge color={badge.color}>{t(badge.labelKey, badge.fallback)}</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Footer */}
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-secondary" onClick={toggle}>
            {t('shared.close', 'Close')}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default ContractCategoryModal;
